import { customBaseQuery } from '@/lib/baseQuery';
import {
  DealContactFolder,
  DealDocumentsResponse,
  DealProcessStatusResponse,
  DocTypeGroup,
  DocumentDetails,
} from '@/pages/mortgage-workflow/types/documents.types';
import { ApiResponse } from '@/shared/types';
import { createApi } from '@reduxjs/toolkit/query/react';
export interface DeleteDocumentRecordDto {
  documentId: string;
  hardDelete?: boolean;
  purgeExternal?: boolean;
  reason?: string;
}

export interface DeleteDocumentResponse {
  id: string;
  deleted: boolean;
  hardDelete: boolean;
  purged: boolean;
}
export const documentsApi = createApi({
  reducerPath: 'documentsApi',
  baseQuery: customBaseQuery,
  tagTypes: ['Documents', 'DealContacts'],
  endpoints: (builder) => ({
    generateSasUrl: builder.mutation<
      ApiResponse<{ url: string }>,
      { containerName: string; blobName: string; minutes?: number }
    >({
      query: (payload) => ({
        url: '/documents/blobs/read',
        method: 'POST',
        body: payload,
      }),
    }),

    downloadAzureFileAsBase64: builder.mutation<
      ApiResponse<{ base64: string }>,
      { url: string }
    >({
      query: ({ url }) => ({
        url: `/documents/download-azure-file-as-base64`,
        method: 'POST',
        body: { url },
      }),
    }),

    getDocumentsByDealId: builder.query<
      ApiResponse<DealDocumentsResponse>,
      { hubspotDealId: string }
    >({
      query: ({ hubspotDealId }) =>
        `/documents/by-hubspot-deal/${hubspotDealId}`,
      providesTags: ['Documents'],
    }),

    deleteDocument: builder.mutation<
      ApiResponse<DeleteDocumentResponse>,
      DeleteDocumentRecordDto
    >({
      query: (body) => ({
        url: `/documents/delete`,
        method: 'POST',
        body,
      }),
    }),

    getDocumentDetailsById: builder.query<
      ApiResponse<DocumentDetails>,
      { documentId: string }
    >({
      query: ({ documentId }) => ({
        url: `/documents/details/${documentId}`,
      }),
    }),

    updateDocumentName: builder.mutation<
      ApiResponse<any>,
      { documentId: string; name: string }
    >({
      query: ({ documentId, name }) => ({
        url: `/documents/update-file-name`,
        method: 'POST',
        body: { documentId, newName: name },
      }),
      invalidatesTags: ['Documents'],
    }),
    updateDocumentType: builder.mutation<
      ApiResponse<any>,
      { documentId: string; type: string }
    >({
      query: ({ documentId, type }) => ({
        url: `/documents/update-document-type`,
        method: 'POST',
        body: { documentId, newDocumentType: type },
      }),
      invalidatesTags: ['Documents'],
    }),
    uploadPdfs: builder.mutation<
      any,
      {
        files: File[];
        parentFolderId: string;
        dealId: string;
        skipCategorize?: boolean;
      }
    >({
      query: ({ files, parentFolderId, dealId, skipCategorize }) => {
        const formData = new FormData();
        files.forEach((file) => {
          formData.append('files', file);
        });
        formData.append('parentFolderId', parentFolderId);
        formData.append('dealId', dealId);
        if (skipCategorize) {
          formData.append('skipCategorize', 'true');
        }
        return {
          url: '/v1/crm-orchestration/upload-pdfs-to-drive',
          method: 'POST',
          body: formData,
        };
      },
    }),
    getDocumentsByDeal: builder.query<
      DocTypeGroup[],
      { hubspotDealId: string }
    >({
      query: ({ hubspotDealId }) => `/documents/by-deal/${hubspotDealId}`,
      transformResponse: (response: ApiResponse<DocTypeGroup[]>) =>
        response.data,
      providesTags: ['Documents'],
    }),

    // Deal-level processing rollup: background poll state + per-document status
    // counts + the five pipeline stages summed across every document. Powers the
    // deal-level status panel on the CA Documents tab.
    getDealProcessStatus: builder.query<
      DealProcessStatusResponse,
      { hubspotDealId: string }
    >({
      query: ({ hubspotDealId }) =>
        `/documents/deal-process-status/${hubspotDealId}`,
      transformResponse: (response: ApiResponse<DealProcessStatusResponse>) =>
        response.data,
      providesTags: ['Documents'],
    }),

    getDealContactsWithDealId: builder.query<
      ApiResponse<any>,
      { dealId: string; assessmentType: string; assessmentId: string }
    >({
      query: ({ dealId, assessmentType, assessmentId }) => ({
        url: `/documents/deal-contacts/${dealId}?assessmentType=${assessmentType}&assessmentId=${assessmentId}`,
      }),
      providesTags: ['DealContacts'],
    }),

    // Resolves the assessment's linked HubSpot deal and returns its contacts
    // (each carrying the file-invite Drive folder). refresh=true rescans HubSpot
    // + Drive; otherwise the cached contacts are returned.
    getDealContactsByAssessment: builder.query<
      ApiResponse<DealContactFolder[]>,
      { assessmentId: string; refresh?: boolean }
    >({
      query: ({ assessmentId, refresh = false }) =>
        `/documents/deal-contacts/by-assessment/${assessmentId}?refresh=${refresh}`,
      providesTags: ['DealContacts'],
    }),

    // Forces a fresh HubSpot + Drive scan and returns the full DealContact[]
    // (incl. newly-added applicants that the cached GET won't surface).
    // Unversioned controller — served at /documents/... not /v1/documents/...
    rescanDealContacts: builder.mutation<
      ApiResponse<any>,
      { dealId: string; assessmentType?: string; assessmentId?: string }
    >({
      query: ({ dealId, assessmentType, assessmentId }) => {
        const params = new URLSearchParams();
        if (assessmentType) params.append('assessmentType', assessmentType);
        if (assessmentId) params.append('assessmentId', assessmentId);
        const qs = params.toString();
        return {
          url: `/documents/deal-contacts/${dealId}/rescan${qs ? `?${qs}` : ''}`,
          method: 'POST',
        };
      },
      invalidatesTags: ['DealContacts'],
    }),
  }),
});

export const {
  useGenerateSasUrlMutation,
  useDownloadAzureFileAsBase64Mutation,
  useGetDocumentsByDealIdQuery,
  useDeleteDocumentMutation,
  useGetDocumentDetailsByIdQuery,
  useLazyGetDocumentDetailsByIdQuery,
  useUpdateDocumentNameMutation,
  useUpdateDocumentTypeMutation,
  useUploadPdfsMutation,
  useGetDocumentsByDealQuery,
  useLazyGetDocumentsByDealQuery,
  useGetDealProcessStatusQuery,
  useGetDealContactsWithDealIdQuery,
  useGetDealContactsByAssessmentQuery,
  useRescanDealContactsMutation,
  useLazyGetDocumentsByDealIdQuery,
} = documentsApi;
